import React, { useState } from 'react';
import { 
  HelpCircle, 
  ChevronDown, 
  ChevronUp, 
  LayoutDashboard, 
  FileText, 
  Bell, 
  Search, 
  Briefcase, 
  CreditCard, 
  Star, 
  User, 
  Shield, 
  Phone, 
  Mail, 
  MessageCircle,
  CheckCircle,
  AlertTriangle,
  Monitor,
  Smartphone,
  LogOut,
  Eye,
  Download,
  TrendingUp,
  Clock,
  BookOpen,
  PlayCircle
} from 'lucide-react';
import climbVideo from '../../assets/videos/climb.mp4';
import { RESULTS_FILINGS_LABELS } from '../../constants/liveIca';

const HelpGuide = () => {
  const [openSection, setOpenSection] = useState('getting-started');
  const [openFaq, setOpenFaq] = useState(null);

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const sections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: <BookOpen size={20} />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Welcome to Climb Capital! Here's how to get started with our investment advisory platform:
          </p>
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
              <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">1</div>
              <div>
                <h4 className="font-semibold text-gray-900">Complete Your Profile</h4>
                <p className="text-sm text-gray-600">Go to your Profile page and update your personal information including PAN card for KYC verification.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
              <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">2</div>
              <div>
                <h4 className="font-semibold text-gray-900">Choose a Subscription Plan</h4>
                <p className="text-sm text-gray-600">Visit the Subscription page to view available plans and choose one that suits your investment needs.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
              <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">3</div>
              <div>
                <h4 className="font-semibold text-gray-900">Add Stocks to Your Watchlist</h4>
                <p className="text-sm text-gray-600">Use Stocks Search to find stocks and add them to "My Stocks" for personalized LIVE announcements.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
              <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">4</div>
              <div>
                <h4 className="font-semibold text-gray-900">Stay Updated</h4>
                <p className="text-sm text-gray-600">Check Notifications and LIVE Announcements regularly for important market updates and recommendations.</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'dashboard',
      title: 'Dashboard Overview',
      icon: <LayoutDashboard size={20} />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Your Dashboard is the central hub showing all important information at a glance:
          </p>
          <ul className="space-y-3">
            <li className="flex items-start gap-2">
              <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={16} />
              <span><strong>Profile Summary:</strong> View your name, subscription status, and KYC verification status.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={16} />
              <span><strong>Subscription Info:</strong> See your current plan, expiry date, and days remaining.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={16} />
              <span><strong>Recent Reports:</strong> Quick access to the latest performance reports.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={16} />
              <span><strong>Your Stocks:</strong> Overview of stocks you're tracking for LIVE announcements.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={16} />
              <span><strong>Quick Actions:</strong> Shortcuts to key features like Profile, Subscription, and Notifications.</span>
            </li>
          </ul>
        </div>
      )
    },
    {
      id: 'performance-reports',
      title: 'Performance Reports',
      icon: <FileText size={20} />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Performance Reports provide detailed analysis and track record of our stock recommendations:
          </p>
          <ul className="space-y-3">
            <li className="flex items-start gap-2">
              <TrendingUp className="text-blue-500 mt-1 flex-shrink-0" size={16} />
              <span><strong>Monthly Reports:</strong> Comprehensive monthly performance summary of all recommendations.</span>
            </li>
            <li className="flex items-start gap-2">
              <Download className="text-blue-500 mt-1 flex-shrink-0" size={16} />
              <span><strong>Download PDF:</strong> Download reports in PDF format for offline viewing and record-keeping.</span>
            </li>
            <li className="flex items-start gap-2">
              <Eye className="text-blue-500 mt-1 flex-shrink-0" size={16} />
              <span><strong>View Online:</strong> Read reports directly in your browser with our built-in PDF viewer.</span>
            </li>
          </ul>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="text-yellow-600 mt-0.5 flex-shrink-0" size={18} />
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> Performance reports are available only for subscribers with an active plan. Past performance does not guarantee future results.
              </p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'announcements',
      title: 'Corporate Announcements',
      icon: <Bell size={20} />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Stay informed about important corporate announcements and market updates:
          </p>
          
          <div className="border rounded-lg p-4 bg-gray-50">
            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <Bell size={18} className="text-blue-600" />
              Important Corporate Announcement
            </h4>
            <p className="text-sm text-gray-600">
              View all corporate announcements from BSE/NSE. Search and filter announcements by company name, type, or date. Useful for tracking filings and disclosures for any listed company.
            </p>
          </div>
          
          <div className="border rounded-lg p-4 bg-green-50 border-green-200">
            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <Bell size={18} className="text-green-600" />
              LIVE Important Corporate Announcement (LIVE ICA)
            </h4>
            <p className="text-sm text-gray-600 mb-3">
              Get real-time Important Corporate Announcements (ICA) directly from BSE for the stocks and indices in your watchlist. You also receive five widely-followed announcement types from every listed company — see "Results &amp; Filings" below.
            </p>
            
            <div className="bg-white border border-green-300 rounded-lg p-3 mb-3">
              <h5 className="font-semibold text-gray-800 text-sm mb-2">📋 How to Use LIVE ICA:</h5>
              <ol className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <span className="bg-green-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                  <span>Go to <strong>Stocks Search</strong> or <strong>Index Search</strong> from the sidebar</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-green-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                  <span>Search for the stocks or indices you want to track</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-green-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                  <span>Click <strong>"Add to My Stocks"</strong> to add them to your watchlist</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-green-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
                  <span>Visit <strong>LIVE ICA</strong> page to see real-time announcements from BSE for your selected stocks and indices</span>
                </li>
              </ol>
            </div>
            
            <div className="bg-white border border-green-300 rounded-lg p-3 mb-3">
              <h5 className="font-semibold text-gray-800 text-sm mb-2 flex items-center gap-2">
                <PlayCircle size={18} className="text-green-600" />
                Video Guide: How to Use LIVE ICA
              </h5>
              <p className="text-sm text-gray-600 mb-3">
                Watch this short walkthrough to see how to search stocks and indices, add them to
                "My Stocks", and view real-time Important Corporate Announcements from BSE on the
                LIVE ICA page.
              </p>
              <div className="rounded-lg overflow-hidden shadow-md bg-black">
                <video
                  src={climbVideo}
                  controls
                  playsInline
                  preload="metadata"
                  className="w-full h-auto"
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>

            <div className="bg-white border border-green-300 rounded-lg p-3 mb-3">
              <h5 className="font-semibold text-gray-800 text-sm mb-2">📊 Results &amp; Filings</h5>
              <p className="text-sm text-gray-600 mb-2">
                Five announcement types reach you from <strong>every listed company</strong>, not just
                your watchlist:
              </p>
              <ul className="text-sm text-gray-600 list-disc list-inside space-y-1 mb-2">
                {RESULTS_FILINGS_LABELS.map((label) => (
                  <li key={label}>{label}</li>
                ))}
              </ul>
              <p className="text-sm text-gray-600">
                Use the <strong>Results &amp; Filings</strong> toggle on the LIVE ICA page to switch
                them off. Turning it off hides these five types from companies{' '}
                <strong>outside your watchlist</strong> only — the stocks and indices you selected
                keep sending you everything, including these five. Announcements you were already
                sent remain accessible from their links.
              </p>
            </div>

            <p className="text-sm text-green-700 font-medium">
              💡 Tip: The more stocks and indices you add to "My Stocks", the more comprehensive your LIVE ICA feed will be. You can add individual stocks, Nifty 50, Sensex, sector indices, and more!
            </p>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="text-orange-600 mt-0.5 flex-shrink-0" size={18} />
              <div>
                <p className="text-sm text-orange-800 font-medium mb-1">Important:</p>
                <p className="text-sm text-orange-700">
                  If you haven't added any stocks or indices to your watchlist, your LIVE ICA page shows only the five Results &amp; Filings types — and switching that toggle off leaves the page empty. Add at least one stock or index from the search pages to get the full LIVE feed from BSE.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'stock-search',
      title: 'Stocks & Index Search',
      icon: <Search size={20} />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Search for stocks and indices to add to your watchlist:
          </p>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Stocks Search</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Search by company name or symbol</li>
                <li>• View stock details and sector</li>
                <li>• Add stocks to your "My Stocks" watchlist</li>
                <li>• Track multiple stocks for LIVE announcements</li>
              </ul>
            </div>
            
            <div className="border rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Index Search</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Search for market indices (Nifty, Sensex, etc.)</li>
                <li>• Track index-level announcements</li>
                <li>• Add indices to your watchlist</li>
                <li>• Get updates on index constituents</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'my-stocks',
      title: 'My Stocks (Watchlist)',
      icon: <Briefcase size={20} />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            "My Stocks" is your personalized watchlist for tracking specific companies:
          </p>
          <ul className="space-y-3">
            <li className="flex items-start gap-2">
              <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={16} />
              <span><strong>Add Stocks:</strong> Use Stocks Search to find and add companies to your watchlist.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={16} />
              <span><strong>View All:</strong> See all stocks and indices you're currently tracking.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={16} />
              <span><strong>Remove Stocks:</strong> Click the remove button to stop tracking a stock.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={16} />
              <span><strong>LIVE Updates:</strong> All stocks in My Stocks will appear in LIVE Announcements.</span>
            </li>
          </ul>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
            <p className="text-sm text-blue-800">
              <strong>How it works:</strong> When you add a stock to My Stocks, you'll receive LIVE corporate announcements for that company in real-time on the LIVE Announcements page.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'subscription',
      title: 'Subscription & Plans',
      icon: <CreditCard size={20} />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Manage your subscription and view available plans:
          </p>
          <ul className="space-y-3">
            <li className="flex items-start gap-2">
              <CreditCard className="text-blue-500 mt-1 flex-shrink-0" size={16} />
              <span><strong>Current Plan:</strong> View your active subscription details, expiry date, and remaining days.</span>
            </li>
            <li className="flex items-start gap-2">
              <TrendingUp className="text-blue-500 mt-1 flex-shrink-0" size={16} />
              <span><strong>Upgrade:</strong> Browse and upgrade to higher plans for more features.</span>
            </li>
            <li className="flex items-start gap-2">
              <Clock className="text-blue-500 mt-1 flex-shrink-0" size={16} />
              <span><strong>Renewal:</strong> Renew your subscription before expiry to avoid service interruption.</span>
            </li>
            <li className="flex items-start gap-2">
              <FileText className="text-blue-500 mt-1 flex-shrink-0" size={16} />
              <span><strong>Payment History:</strong> View your past payments and invoices.</span>
            </li>
          </ul>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
            <p className="text-sm text-green-800">
              <strong>Free Trial:</strong> New users get a 2-month free trial of Basic plan features to explore the platform.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'notifications',
      title: 'Notifications',
      icon: <Bell size={20} />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Stay updated with important announcements from Climb Capital:
          </p>
          <ul className="space-y-3">
            <li className="flex items-start gap-2">
              <Bell className="text-blue-500 mt-1 flex-shrink-0" size={16} />
              <span><strong>Admin Updates:</strong> Important announcements, market insights, and platform updates.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="text-blue-500 mt-1 flex-shrink-0" size={16} />
              <span><strong>Read Status:</strong> Notifications show as read/unread. Click to mark as read.</span>
            </li>
            <li className="flex items-start gap-2">
              <Eye className="text-blue-500 mt-1 flex-shrink-0" size={16} />
              <span><strong>Mark All Read:</strong> Use the "Mark All Read" button to clear all unread notifications.</span>
            </li>
            <li className="flex items-start gap-2">
              <Bell className="text-blue-500 mt-1 flex-shrink-0" size={16} />
              <span>
                <strong>Results &amp; Filings:</strong> The toggle on the LIVE ICA page controls both the
                feed and the notifications for those five announcement types. Switch it off and you
                stop being notified about them for companies outside your watchlist — your selected
                stocks keep notifying you as usual.
              </span>
            </li>
          </ul>
        </div>
      )
    },
    {
      id: 'profile',
      title: 'Profile & KYC',
      icon: <User size={20} />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Manage your profile and complete KYC verification:
          </p>
          <ul className="space-y-3">
            <li className="flex items-start gap-2">
              <User className="text-blue-500 mt-1 flex-shrink-0" size={16} />
              <span><strong>Personal Info:</strong> Update your name, WhatsApp number, and profile picture.</span>
            </li>
            <li className="flex items-start gap-2">
              <Shield className="text-blue-500 mt-1 flex-shrink-0" size={16} />
              <span><strong>KYC Verification:</strong> Submit your PAN card and date of birth for verification.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="text-blue-500 mt-1 flex-shrink-0" size={16} />
              <span><strong>KYC Status:</strong> Track your KYC status (Pending, Under Review, Verified, or Rejected).</span>
            </li>
          </ul>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
            <div className="flex items-start gap-2">
              <Shield className="text-yellow-600 mt-0.5 flex-shrink-0" size={18} />
              <p className="text-sm text-yellow-800">
                <strong>Why KYC?</strong> As per SEBI regulations, investment advisors must verify client identity. Complete KYC to access all features.
              </p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'security',
      title: 'Account Security',
      icon: <Shield size={20} />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Your account security is our priority:
          </p>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <Monitor size={18} className="text-blue-600" />
              Single Device Login
            </h4>
            <p className="text-sm text-gray-600">
              For security, you can only be logged in on one device at a time. Logging in on a new device will automatically log you out from other devices.
            </p>
          </div>
          
          <ul className="space-y-3 mt-4">
            <li className="flex items-start gap-2">
              <LogOut className="text-red-500 mt-1 flex-shrink-0" size={16} />
              <span><strong>Session Expired:</strong> If logged in elsewhere, you'll see a "Session Expired" message and need to log in again.</span>
            </li>
            <li className="flex items-start gap-2">
              <Shield className="text-green-500 mt-1 flex-shrink-0" size={16} />
              <span><strong>Change Password:</strong> Regularly update your password from the Profile page.</span>
            </li>
            <li className="flex items-start gap-2">
              <Smartphone className="text-blue-500 mt-1 flex-shrink-0" size={16} />
              <span><strong>Secure Login:</strong> Never share your login credentials with anyone.</span>
            </li>
          </ul>
        </div>
      )
    }
  ];

  const faqs = [
    {
      question: "How do I subscribe to a plan?",
      answer: "Go to the Subscription page from the sidebar. Browse available plans, select the one that suits you, and complete the payment using Razorpay. Your subscription will be activated immediately after successful payment."
    },
    {
      question: "What is the difference between 'Corporate Announcement' and 'LIVE Corporate Announcement'?",
      answer: "Corporate Announcement shows all announcements from all listed companies. LIVE Corporate Announcement shows real-time updates for stocks you've added to 'My Stocks' watchlist, plus five Results & Filings types from every listed company. Add stocks first, then check LIVE announcements for personalized updates."
    },
    {
      question: "What does the 'Results & Filings' toggle do?",
      answer: "It controls five announcement types — Investor Presentation, Earnings Call Transcript, Annual Reports (Reg. 53), Financial Results (Reg. 52) and Outcome of Board Meeting — that you normally receive from every listed company. Switch it off and you stop seeing and being notified about them for companies outside your watchlist. Your selected stocks and indices are never affected: you keep receiving everything about them, these five included."
    },
    {
      question: "My LIVE ICA page is empty. What happened?",
      answer: "This happens when you have no stocks or indices in your watchlist and the 'Results & Filings' toggle is switched off, which leaves nothing to show. Either turn the toggle back on, or add stocks and indices from the Stocks Search and Index Search pages."
    },
    {
      question: "How do I add stocks to my watchlist?",
      answer: "Go to Stocks Search, find the company you want to track, and click the 'Add' button. The stock will appear in 'My Stocks' and you'll receive LIVE announcements for it."
    },
    {
      question: "Why am I seeing 'Session Expired' message?",
      answer: "For security, we allow login on only one device at a time. If someone (including you) logged in from another device, your current session expires. Simply log in again to continue."
    },
    {
      question: "How do I complete KYC verification?",
      answer: "Go to Profile page, enter your PAN card number and date of birth, then submit. Our team will verify your details within 2-3 business days. You'll see the status update in your profile."
    },
    {
      question: "Can I download performance reports?",
      answer: "Yes! Go to Performance Reports, find the report you want, and click the Download button to save it as a PDF. You can also view it online using the View button."
    },
    {
      question: "How do I change my password?",
      answer: "Go to Profile page and click on 'Change Password'. Enter your current password and new password, then submit. You'll need to log in again with your new password."
    },
    {
      question: "What happens when my subscription expires?",
      answer: "When your subscription expires, you'll lose access to premium features like Performance Reports and LIVE Announcements. You can renew anytime from the Subscription page to restore access."
    },
    {
      question: "How do I contact support?",
      answer: "You can reach us via WhatsApp at +91 93722 73366 or email at info@climbcapital.in. Our support team is available Monday to Saturday, 9 AM to 6 PM IST."
    },
    {
      question: "Is my payment information secure?",
      answer: "Yes, all payments are processed securely through Razorpay, a PCI-DSS compliant payment gateway. We never store your card details on our servers."
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white/20 rounded-lg">
            <HelpCircle size={28} />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Help & User Guide</h1>
            <p className="text-blue-100 text-sm">Learn how to use Climb Capital platform effectively</p>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="bg-white rounded-xl shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Quick Links</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <a href="#getting-started" onClick={() => setOpenSection('getting-started')} 
             className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors text-blue-700 text-sm font-medium">
            <BookOpen size={18} />
            Getting Started
          </a>
          <a href="#subscription" onClick={() => setOpenSection('subscription')}
             className="flex items-center gap-2 p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors text-green-700 text-sm font-medium">
            <CreditCard size={18} />
            Subscription
          </a>
          <a href="#my-stocks" onClick={() => setOpenSection('my-stocks')}
             className="flex items-center gap-2 p-3 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors text-yellow-700 text-sm font-medium">
            <Briefcase size={18} />
            My Stocks
          </a>
          <a href="#security" onClick={() => setOpenSection('security')}
             className="flex items-center gap-2 p-3 bg-red-50 rounded-lg hover:bg-red-100 transition-colors text-red-700 text-sm font-medium">
            <Shield size={18} />
            Security
          </a>
        </div>
      </div>

      {/* Guide Sections */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <h2 className="text-lg font-bold text-gray-900 p-6 border-b">User Guide</h2>
        <div className="divide-y">
          {sections.map((section) => (
            <div key={section.id} id={section.id}>
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                    {section.icon}
                  </div>
                  <span className="font-semibold text-gray-900">{section.title}</span>
                </div>
                {openSection === section.id ? (
                  <ChevronUp className="text-gray-400" size={20} />
                ) : (
                  <ChevronDown className="text-gray-400" size={20} />
                )}
              </button>
              {openSection === section.id && (
                <div className="px-6 pb-6 pt-2 bg-gray-50 border-t">
                  {section.content}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* FAQs Section */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <h2 className="text-lg font-bold text-gray-900 p-6 border-b">Frequently Asked Questions</h2>
        <div className="divide-y">
          {faqs.map((faq, index) => (
            <div key={index}>
              <button
                onClick={() => toggleFaq(index)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left"
              >
                <span className="font-medium text-gray-900 pr-4">{faq.question}</span>
                {openFaq === index ? (
                  <ChevronUp className="text-gray-400 flex-shrink-0" size={20} />
                ) : (
                  <ChevronDown className="text-gray-400 flex-shrink-0" size={20} />
                )}
              </button>
              {openFaq === index && (
                <div className="px-4 pb-4 pt-0">
                  <p className="text-gray-600 bg-blue-50 p-4 rounded-lg">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Contact Support Section */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl shadow-lg p-6 text-white">
        <h2 className="text-xl font-bold mb-4">Still Need Help?</h2>
        <p className="text-gray-300 mb-6">
          Our support team is here to assist you. Reach out through any of these channels:
        </p>
        <div className="grid md:grid-cols-3 gap-4">
          <a 
            href="https://api.whatsapp.com/send/?phone=919372273366&text=Hi%2C%20I%20need%20help%20with%20Climb%20Capital" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-4 bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
          >
            <MessageCircle size={24} />
            <div>
              <p className="font-semibold">WhatsApp</p>
              <p className="text-sm text-green-100">+91 93722 73366</p>
            </div>
          </a>
          
          <a 
            href="mailto:info@climbcapital.in"
            className="flex items-center gap-3 p-4 bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Mail size={24} />
            <div>
              <p className="font-semibold">Email</p>
              <p className="text-sm text-blue-100">info@climbcapital.in</p>
            </div>
          </a>
          
          <a 
            href="tel:+919372273366"
            className="flex items-center gap-3 p-4 bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Phone size={24} />
            <div>
              <p className="font-semibold">Call Us</p>
              <p className="text-sm text-purple-100">+91 93722 73366</p>
            </div>
          </a>
        </div>
        <p className="text-gray-400 text-sm mt-4">
          Support hours: Monday to Saturday, 9:00 AM - 6:00 PM IST
        </p>
      </div>

      {/* Disclaimer */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="text-yellow-600 mt-0.5 flex-shrink-0" size={24} />
          <div>
            <h3 className="font-semibold text-yellow-800 mb-2">Important Disclaimer</h3>
            <p className="text-sm text-yellow-700">
              Investments in securities market are subject to market risks. Read all the related documents carefully before investing. 
              The securities quoted are for illustration only and are not recommendatory. Past performance is not indicative of future results. 
              Climb Capital is a SEBI Registered Investment Advisor (INA000018154).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpGuide;
